import { Injectable } from '@angular/core';
import {
	HttpInterceptor,
	HttpHeaders,
	HttpRequest,
	HttpHandler,
	HttpEvent,
	HttpErrorResponse,
	HttpResponse
} from '@angular/common/http';
import { Router } from '@angular/router';

import { DynamicScriptingService } from '@nx-ahs/script-shared/services';
import { TimeoutService } from '@nx-ahs/timeout';

import { Observable } from 'rxjs';
import { mergeMap, tap } from 'rxjs/operators';

import { SecurityService } from '../services/security.service';
import { AppCoreService } from '../services/app-core.service';
import { AppVersionService } from '../services/app-version.service';

import { environment } from '../../../environments/environment';

@Injectable({
	providedIn: 'root'
})
export class AppHttpInterceptor implements HttpInterceptor {
	// Add any URLs that should be ignored by the interceptor
	private ignoreUrls: string[] = [
		environment.apiUrl + 'api/Portal/Login',
		environment.apiUrl + 'api/Portal/VerifyEmail',
		environment.apiUrl + 'api/Portal/SendForgotPassword',
		environment.apiUrl + 'api/Portal/ResetPassword',
		environment.apiUrl + 'api/Portal/ForgotPasswordReset',
		environment.apiUrl + 'api/Portal/RetrieveUsername',
		environment.apiUrl + 'api/Portal/Register',
		environment.apiUrl + 'api/Portal/ReSendVerificationEmail',
		environment.apiUrl + 'api/Main/GetPortalApplication',
		environment.apiUrl + 'api/Portal/GetClientConfig',
		environment.apiUrl + 'api/Portal/GetWarningResourceCultureStrings',
		environment.apiUrl + 'api/Portal/GetPolicyResourceCultureStrings',
		environment.apiUrl + 'api/Main/GetToken',
		environment.apiUrl + 'api/Main/VerifyPortalAPIStatus',
		environment.apiUrl + 'api/Main/GetFeatureFlags',
		environment.apiUrl + 'api/Portal/GetLanguagesForPublicPage',
		environment.apiUrl + 'api/Contract/GetClientLanguage',
		environment.apiUrl + 'api/Portal/TrackForgotPasswordLinkInteraction',
		environment.apiUrl + 'api/Portal/VerifyResetPasswordCode',
		environment.apiUrl + 'api/Portal/GetAppInsightsConfig',
		environment.apiUrl + 'health',
		'https://atlas.microsoft.com' // Azure Maps - uses its own authentication
	];

	private ignoreUrlsUntilLoggedIn: string[] = [
		environment.apiUrl + 'api/Contract/GetDefinitionsVersions',
		environment.apiUrl + 'api/Contract/SaveDefinitionValue'
	];

	private ignore500Urls: string[] = [environment.apiUrl + 'api/Main/UploadUsers'];

	// Add any URLs called without user interaction. They should not prevent the app from timing out
	private silentURLs: string[] = [
		environment.apiUrl + 'api/Portal/RefreshToken',
		environment.apiUrl + 'api/Main/GetIcon',
		environment.apiUrl + 'api/Main/GetToken',
		//environment.apiUrl + 'api/Portal/GetWarningVerbiageData',
		'https://login.microsoftonline.com'
	];

	constructor(
		private appCoreService: AppCoreService,
		private router: Router,
		private securityService: SecurityService,
		private timeoutService: TimeoutService,
		private dynamicScriptService: DynamicScriptingService,
		private versionService: AppVersionService
	) {}

	intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		// Keep track of every http request to prevent application from timing out (unless it's in the silent list)
		if (
			!this.silentURLs.some((x) => {
				return request.url.startsWith(x);
			})
		) {
			this.timeoutService.actionTaken();
		}

		let mergedIgnoreUrls = this.ignoreUrls.concat([]);

		if (!this.securityService.isAuthenticated()) {
			mergedIgnoreUrls = mergedIgnoreUrls.concat(this.ignoreUrlsUntilLoggedIn);
		}

		const urlWithoutParams = request.url.split('?')[0];
		const ignoreUrlFound = mergedIgnoreUrls.some((p) => urlWithoutParams.toLowerCase().includes(p.toLowerCase()));

		if (!ignoreUrlFound) {
			return this.getToken(request).pipe(
				mergeMap((result) => {
					let newHeaders = this.createNewHeaders(request, result);

					// Always add the version header
					const version = this.versionService.getVersion();
					newHeaders = newHeaders.set('X-App-Version', version);

					const authReq = request.clone({ headers: newHeaders });

					return next.handle(authReq).pipe(
						tap(
							(event: HttpEvent<any>) => {
								if (event instanceof HttpResponse) {
									// Event handling placeholder
								}
							},
							(err: any) => {
								if (err instanceof HttpErrorResponse) {
									if (err.status === 401) {
										if (this.securityService.isAuthenticated()) {
											this.router.navigate(['error/401']);
										} else {
											this.router.navigate(['security/login']);
										}
									} else if (err.status === 403) {
										this.router.navigate(['error/403']);
									} else if (err.status === 426) {
										// Upgrade Required - prompt user to refresh for new version
										this.versionService.promptVersionRefresh();
									} else if (
										err.status === 500 &&
										!this.ignore500Urls.some((x) => {
											return err.url.startsWith(x);
										})
									) {
										this.router.navigate(['error/500']);
									}
								}
							}
						)
					);
				})
			);
		} else {
			const headerSettings: Record<string, string | string[]> = {};
			for (const key of request.headers.keys()) {
				headerSettings[key] = request.headers.getAll(key);
			}

			// Add the selected application id
			if (this.appCoreService.currentApplication) {
				headerSettings['x-ht-appId'] = this.appCoreService.currentApplication.appId.toString();
				headerSettings['PortalTypeId'] = this.appCoreService.currentApplication.portalTypeId.toString();
			}

			headerSettings['IsPortalBearer'] = 'true';

			// Always add the version header
			const version = this.versionService.getVersion();
			headerSettings['X-App-Version'] = version;

			const newHeaders = new HttpHeaders(headerSettings);
			const updatedRequest = request.clone({ headers: newHeaders });

			return next.handle(updatedRequest).pipe(
				tap({
					error: (err: any) => {
						if (err instanceof HttpErrorResponse && err.status === 426) {
							// Upgrade Required - prompt user to refresh for new version
							this.versionService.promptVersionRefresh();
						}
					}
				})
			);
		}
	}

	private createNewHeaders(request: HttpRequest<any>, token: string) {
		const headerSettings: Record<string, string | string[]> = {};
		for (const key of request.headers.keys()) {
			headerSettings[key] = request.headers.getAll(key);
		}

		// Add Authorization header with Bearer Token
		if (token) {
			headerSettings['Authorization'] = 'Bearer ' + token;
		}

		// Add the selected application id
		headerSettings['x-ht-appId'] = this.appCoreService.currentApplication.appId.toString();

		// Add portaltypeid
		headerSettings['PortalTypeId'] = this.appCoreService.currentApplication.portalTypeId.toString();

		headerSettings['IsPortalBearer'] = 'true';

		let newHeader = new HttpHeaders(headerSettings);

		// Always add the version header
		const version = this.versionService.getVersion();
		newHeader = newHeader.set('X-App-Version', version);

		return newHeader;
	}

	private getToken(request: HttpRequest<any>): Observable<string> {
		return this.securityService.acquireToken(request.url);
	}
}
